// plan-13 S04: ghost-node panel — stage widget "nodes"

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { utcMinute } from '../../core/seed'
import { getRoster, ensureVisitorAlias } from './roster'
import { utcNow } from '../../core/time'

export const nodesWidget: StageWidget = {
  id: 'nodes',
  label: 'CONSENSUS NETWORK // SIMULATED NODES',

  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const visitorAlias = ensureVisitorAlias()
    const minuteKey = utcMinute()
    const roster = getRoster(minuteKey, visitorAlias)

    const container = document.createElement('div')
    container.className = 'vt-widget-body vt-nodes-container'
    container.style.cssText = `
      font-family: var(--vt-font-mono);
      font-size: clamp(10px, 0.95vw, 12px);
      color: var(--vt-term-green);
      padding: 12px;
      line-height: 1.32;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      min-height: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    `

    // header
    const header = document.createElement('div')
    header.style.cssText = `
      color: var(--vt-green-dim);
      margin-bottom: 8px;
      border-bottom: 1px solid var(--vt-green-dim);
      padding-bottom: 4px;
    `
    header.textContent = `@consensus.network · seed: ${minuteKey} · quorum: 48/48`
    container.appendChild(header)

    // visitor row
    const visitorRow = createRow(
      '[ you ]',
      visitorAlias,
      'active',
      'present',
      true,
    )
    visitorRow.style.cursor = 'pointer'
    visitorRow.addEventListener('click', () => {
      ctx.emitOracle(`> alias: ${visitorAlias}. observer: present. quorum: 48 of 48.`)
    })
    container.appendChild(visitorRow)

    // separator
    const sep1 = document.createElement('div')
    sep1.style.cssText = 'border-bottom: 1px solid var(--vt-outline); margin: 4px 0;'
    container.appendChild(sep1)

    // ghost rows
    const rows: HTMLElement[] = []
    for (const ghost of roster) {
      const { hh, mm, ss } = formatActivityTime(ghost.lastActivity.at)
      let activityText = ghost.lastActivity.kind
      if (ghost.lastActivity.payload) activityText += ` ${ghost.lastActivity.payload}`

      const row = createRow(
        '',
        ghost.alias,
        ghost.status,
        `${activityText} · ${hh}:${mm}:${ss} utc`,
        false,
      )
      row.style.cursor = 'pointer'
      row.addEventListener('click', () => {
        ctx.emitOracle(`> ${ghost.alias}: ${activityText}.`)
      })
      container.appendChild(row)
      rows.push(row)
    }

    // separator
    const sep2 = document.createElement('div')
    sep2.style.cssText = 'border-bottom: 1px solid var(--vt-outline); margin: 4px 0;'
    container.appendChild(sep2)

    // footer — transparency clause (non-removable)
    const footer = document.createElement('div')
    footer.style.cssText = 'color: var(--vt-green-dim); font-style: italic; margin-top: 4px;'
    footer.textContent = '// simulated. seed: utc_minute. verify: console > seed.utcMinute()'
    container.appendChild(footer)

    host.appendChild(container)

    return {
      dismiss() {
        container.remove()
      },
    }
  },
}

function createRow(
  label: string,
  alias: string,
  status: string,
  activity: string,
  isVisitor: boolean,
): HTMLElement {
  const row = document.createElement('div')
  row.style.cssText = `
    display: flex;
    gap: 12px;
    padding: 3px 4px;
    border-bottom: 1px solid rgba(26, 26, 26, 0.5);
    align-items: center;
  `
  if (isVisitor) {
    row.style.color = 'var(--vt-amber)'
  }

  const labelEl = document.createElement('span')
  labelEl.style.cssText = `min-width: 60px; color: ${isVisitor ? 'var(--vt-amber)' : 'var(--vt-green-dim)'};`
  labelEl.textContent = label
  row.appendChild(labelEl)

  const aliasEl = document.createElement('span')
  aliasEl.style.cssText = 'min-width: 100px;'
  aliasEl.textContent = alias
  row.appendChild(aliasEl)

  const statusEl = document.createElement('span')
  statusEl.style.cssText = 'min-width: 70px; color: var(--vt-green-dim);'
  statusEl.textContent = status
  row.appendChild(statusEl)

  const actEl = document.createElement('span')
  actEl.style.cssText = 'flex: 1; color: var(--vt-green-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
  actEl.textContent = `last: ${activity}`
  row.appendChild(actEl)

  return row
}

function formatActivityTime(_atMs: number): { hh: string; mm: string; ss: string } {
  return utcNow()
}
